/**
 * Load the bundled Service Root public key.
 *
 * The JSON file is generated by `dovach-op init` (Go CLI) and committed to
 * the repo. At build time, Vite reads this file and inlines its contents so
 * the SR_pk constant is baked into shipped artefacts — exactly what makes
 * the trust model work (the cloud cannot change what's compiled in).
 */

import { decodeBase64 } from "tweetnacl-util";

import sourceJson from "../sr-pk.json" with { type: "json" };

export class SRPublicKeyError extends Error {
  override name = "SRPublicKeyError";
}

interface SRPublicKeyJson {
  version: number;
  key_b64: string;
  issued_at: string | null;
  comment?: string;
}

const SR_PK_PLACEHOLDER = "PLACEHOLDER_NOT_YET_GENERATED";

/** True if the bundled file is still the unreplaced placeholder. */
export function isPlaceholder(): boolean {
  return (sourceJson as SRPublicKeyJson).key_b64 === SR_PK_PLACEHOLDER;
}

/**
 * Decode SR_pk from the bundled JSON. Throws if the placeholder hasn't been
 * replaced (i.e. `dovach-op init` was never run). Throws on malformed key.
 */
export function loadSRPublicKey(): Uint8Array {
  const j = sourceJson as SRPublicKeyJson;
  if (j.key_b64 === SR_PK_PLACEHOLDER) {
    throw new SRPublicKeyError(
      "SR_pk is the placeholder — run `dovach-op init` to generate one before building.",
    );
  }
  let bytes: Uint8Array;
  try {
    bytes = decodeBase64(j.key_b64);
  } catch (err) {
    throw new SRPublicKeyError(`SR_pk is not valid base64: ${(err as Error).message}`);
  }
  if (bytes.length !== 32) {
    throw new SRPublicKeyError(
      `SR_pk must be 32 bytes (Ed25519 pubkey); got ${bytes.length}.`,
    );
  }
  return bytes;
}
